import React from 'react';
import { Link } from 'react-router-dom';
import { ChatMarkdown } from './ChatMarkdown';

export interface IChatSource {
    type: 'object' | 'survey' | 'asset';
    id: string;
    displayName: string;
    url: string;
}

interface IChatMessageProps {
    role: 'user' | 'assistant';
    content: string;
    sources?: IChatSource[] | undefined;
    isInterrupted?: boolean | undefined;
    // Render labels in user's language. Passed in from ChatPanel so this
    // component itself can stay translate-context-free.
    youLabel: string;
    aiLabel: string;
    sourcesLabel: string;
    interruptedLabel: string;
}

// User messages render as plain text; assistant messages go through
// the strict-allowlist markdown renderer (ChatMarkdown).
//
// Object/survey URLs use <Link> for SPA navigation; a plain <a> would
// drop Redux state. Asset URLs use <a target="_blank"> for downloads.
export function ChatMessage({
    role,
    content,
    sources,
    isInterrupted,
    youLabel,
    aiLabel,
    sourcesLabel,
    interruptedLabel,
}: IChatMessageProps): JSX.Element {
    const isUser = role === 'user';
    return (
        <div
            style={{
                marginBottom: 12,
                padding: '8px 12px',
                borderRadius: 8,
                backgroundColor: isUser ? '#e3f2fd' : '#ffffff',
                border: isUser ? '1px solid #bbdefb' : '1px solid #e5e5e5',
                maxWidth: '85%',
                marginLeft: isUser ? 'auto' : 0,
            }}
        >
            <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>{isUser ? youLabel : aiLabel}</div>
            <div style={{ lineHeight: 1.5 }}>
                {isUser ? <div style={{ whiteSpace: 'pre-wrap' }}>{content}</div> : <ChatMarkdown content={content} />}
            </div>
            {isInterrupted && !isUser && (
                <div style={{ fontSize: 11, color: '#c00', marginTop: 6 }}>
                    <i className="fa fa-exclamation-triangle" style={{ marginRight: 4 }} />
                    {interruptedLabel}
                </div>
            )}
            {!isUser && sources && sources.length > 0 && (
                <div style={{ marginTop: 8, paddingTop: 6, borderTop: '1px solid #eee', fontSize: 11 }}>
                    <div style={{ color: '#888', marginBottom: 4 }}>{sourcesLabel}:</div>
                    {/* Chips, not a comma-list — display names contain commas. */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {sources.map((s) => {
                            const chipStyle: React.CSSProperties = {
                                display: 'inline-block',
                                padding: '2px 8px',
                                border: '1px solid #d0d7de',
                                borderRadius: 12,
                                backgroundColor: '#f6f8fa',
                                color: '#0366d6',
                                textDecoration: 'none',
                            };
                            return s.type === 'asset' ? (
                                <a key={`${s.type}-${s.id}`} href={s.url} target="_blank" rel="noopener noreferrer" style={chipStyle}>
                                    {s.displayName}
                                </a>
                            ) : (
                                <Link key={`${s.type}-${s.id}`} to={s.url} style={chipStyle}>
                                    {s.displayName}
                                </Link>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
